import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import Swal from 'sweetalert2';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AddPopupComponent } from '../add-popup/add-popup.component';

declare const $: any;

@Component({
  selector: 'app-user-checks-table',
  standalone: true,
  imports: [CommonModule, FormsModule, AddPopupComponent],
  templateUrl: './user-checks-table.component.html',
  styleUrl: './user-checks-table.component.css'
})
export class UserChecksTableComponent implements OnInit, OnChanges {
  showSkeleton = true;
  showDateModal = false;
  @Input() leaveSetDate: string | null = null;
  @Input() deviceCheck = false;
  @Input() leaveCheck = false;
  @Input() authKey = false;
  @Input() keysId: any = null;
  @Input() loading = false;

  prevDeviceCheck = false;
  prevLeaveCheck = false;
  prevAuthKey = false;

  @Output() saveChecks = new EventEmitter<{ id?: any, device_check: boolean, leave_check: boolean, auth_key: boolean, leave_date: string | null }>();
  @Output() deleteChecks = new EventEmitter<any>();

   ngOnChanges() {
    if (this.loading) {
      this.showTableLoader();
    }
    // keep track of previous values when inputs change from parent
    this.prevDeviceCheck = !!this.deviceCheck;
    this.prevLeaveCheck = !!this.leaveCheck;
    this.prevAuthKey = !!this.authKey;
  }
  ngOnInit(): void {
    this.showTableLoader();
    this.prevDeviceCheck = !!this.deviceCheck;
    this.prevLeaveCheck = !!this.leaveCheck;
    this.prevAuthKey = !!this.authKey;
  }
  skeletonRows: any[] = [];

  showTableLoader(duration = 800) {
    this.showSkeleton = true;
    const rowCount = this.leaveSetDate?.length || 3;
    this.skeletonRows = Array(rowCount).fill(0);
    setTimeout(() => {
      this.showSkeleton = false;
    }, duration);
  }

  toggleDateModal() {
    this.showDateModal = !this.showDateModal;
  }
  getFormattedDate(): string {
    if (!this.leaveSetDate) return 'Select Date';

    const date = new Date(this.leaveSetDate);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  onSaveDate() {
    this.onSaveChecks();
    this.toggleDateModal();
  }
 
  onSaveChecks() {
    this.saveChecks.emit({
      id: this.keysId,
      device_check: this.deviceCheck,
      leave_check: this.leaveCheck,
      auth_key: this.authKey,
      leave_date: this.leaveSetDate ?? null
    });
  }

  // Device check modal flow similar to team-management's 2FA modal
  intendedDeviceStatus = false;
  toggleRefDevice: HTMLInputElement | null = null;

  onDeviceClick(event: Event, toggleRef: HTMLInputElement) {
    event.preventDefault();
    this.intendedDeviceStatus = toggleRef.checked;
    this.toggleRefDevice = toggleRef;

    // show bootstrap modal
    setTimeout(() => {
      $('#confirmModalDevice').modal('show');
    }, 0);
  }

  confirmDeviceToggle() {
    const isEnabled = this.intendedDeviceStatus;
    this.prevDeviceCheck = isEnabled;
    if (this.toggleRefDevice) {
      this.toggleRefDevice.checked = isEnabled;
      this.deviceCheck = isEnabled;
    }
    this.onSaveChecks();
    $('#confirmModalDevice').modal('hide');
  }

  cancelDeviceToggle() {
    if (this.toggleRefDevice) {
      // revert the checkbox to previous value
      this.toggleRefDevice.checked = !this.intendedDeviceStatus;
      this.deviceCheck = !this.intendedDeviceStatus;
    }
    $('#confirmModalDevice').modal('hide');
  }

  // Leave check modal flow
  intendedLeaveStatus = false;
  toggleRefLeave: HTMLInputElement | null = null;

  onLeaveClick(event: Event, toggleRef: HTMLInputElement) {
    event.preventDefault();
    this.intendedLeaveStatus = toggleRef.checked;
    this.toggleRefLeave = toggleRef;
    setTimeout(() => {
      $('#confirmModalLeave').modal('show');
    }, 0);
  }

  confirmLeaveToggle() {
    const isEnabled = this.intendedLeaveStatus;
    this.prevLeaveCheck = isEnabled;
    if (this.toggleRefLeave) {
      this.toggleRefLeave.checked = isEnabled;
      this.leaveCheck = isEnabled;
    }
    this.onSaveChecks();
    $('#confirmModalLeave').modal('hide');
  }

  cancelLeaveToggle() {
    if (this.toggleRefLeave) {
      this.toggleRefLeave.checked = !this.intendedLeaveStatus;
      this.leaveCheck = !this.intendedLeaveStatus;
    }
    $('#confirmModalLeave').modal('hide');
  }

  // Auth (2FA) modal flow
  intendedAuthStatus = false;
  toggleRefAuth: HTMLInputElement | null = null;

  onAuthClick(event: Event, toggleRef: HTMLInputElement) {
    event.preventDefault();
    this.intendedAuthStatus = toggleRef.checked;
    this.toggleRefAuth = toggleRef;
    setTimeout(() => {
      $('#confirmModalAuth').modal('show');
    }, 0);
  }

  confirmAuthToggle() {
    const isEnabled = this.intendedAuthStatus;
    this.prevAuthKey = isEnabled;
    if (this.toggleRefAuth) {
      this.toggleRefAuth.checked = isEnabled;
      this.authKey = isEnabled;
    }
    this.onSaveChecks();
    $('#confirmModalAuth').modal('hide');
  }

  cancelAuthToggle() {
    if (this.toggleRefAuth) {
      this.toggleRefAuth.checked = !this.intendedAuthStatus;
      this.authKey = !this.intendedAuthStatus;
    }
    $('#confirmModalAuth').modal('hide');
  }

  onDeviceToggle() {
    const newVal = this.deviceCheck;
    const action = newVal ? 'Enable' : 'Disable';
    Swal.fire({
      title: `${action} Device Check?`,
      text: `Are you sure you want to ${action.toLowerCase()} Device Check?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.prevDeviceCheck = newVal;
        this.onSaveChecks();
      } else {
        this.deviceCheck = this.prevDeviceCheck;
      }
    });
  }

  onLeaveToggle() {
    const newVal = this.leaveCheck;
    const action = newVal ? 'Enable' : 'Disable';
    Swal.fire({
      title: `${action} Leave Check?`,
      text: `Are you sure you want to ${action.toLowerCase()} Leave Check?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.prevLeaveCheck = newVal;
        this.onSaveChecks();
      } else {
        this.leaveCheck = this.prevLeaveCheck;
      }
    });
  }

  onAuthToggle() {
    const newVal = this.authKey;
    const action = newVal ? 'Enable' : 'Disable';
    const userLabel = '';
    Swal.fire({
      title: `${action} 2FA?`,
      text: `Are you sure you want to ${action.toLowerCase()} 2FA${userLabel ? ' for ' + userLabel : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.prevAuthKey = newVal;
        this.onSaveChecks();
      } else {
        this.authKey = this.prevAuthKey;
      }
    });
  }

  onDeleteClick() {
    if (this.keysId == null) {
      return;
    }
    if (!confirm('Are you sure you want to delete this keys configuration?')) {
      return;
    }
    this.deleteChecks.emit(this.keysId);
  }
}
